import React, { useState } from 'react';
import { Box, IconButton, InputLabel, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Modal from '@Components/Modal';
import SbButton from '../../components/Button';
import API from '@Services/apiAxios';
import { useSnackbar } from 'notistack';

interface CancelCarrierBookingModalProps {
  open: boolean;
  onClose: () => void;
  shipmentId: string;
  shipmentNumber?: string;
  onSuccess: () => void;
}

export default function CancelCarrierBookingModal({
  open,
  onClose,
  shipmentId,
  shipmentNumber,
  onSuccess,
}: CancelCarrierBookingModalProps) {
  const [cancelReason, setCancelReason] = useState('');
  const [cancelReasonTouched, setCancelReasonTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  const handleClose = () => {
    if (isSubmitting) return;
    setCancelReason('');
    setCancelReasonTouched(false);
    setSubmitError('');
    onClose();
  };

  const handleConfirm = async () => {
    setCancelReasonTouched(true);
    if (!cancelReason.trim()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const res = await API.post('/squad/task/cancelCarrierBooking', {
        _shipmentId: shipmentId,
        cancelReason: cancelReason.trim(),
      });
      const successMsg = res.data?.data?.msg || res.data?.msg || 'Carrier booking cancelled successfully!';
      enqueueSnackbar(successMsg, { variant: 'success' });
      onSuccess();
      handleClose();
    } catch (err: any) {
      console.error('Error cancelling carrier booking:', err);
      const errorMsg =
        err.response?.data?.data?.msg || err.response?.data?.msg || err.message || 'Something went wrong';
      setSubmitError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasError = cancelReasonTouched && !cancelReason.trim();

  return (
    <Modal open={open} onClose={handleClose} maxWidth="sm">
      <Box sx={{ position: 'relative', width: '100%', minWidth: { sm: '400px' } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 600 }}>
            Cancel Carrier Booking
          </Typography>
          <IconButton onClick={handleClose} disabled={isSubmitting} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          This shipment <strong>{shipmentNumber}</strong> has already been assigned to a third-party carrier. Please
          provide a cancellation reason before proceeding. This action cannot be undone.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
          <InputLabel
            htmlFor="cancel-reason"
            required
            sx={{ fontSize: '14px', fontWeight: 500, color: 'text.secondary' }}
          >
            Cancellation Reason
          </InputLabel>
          <TextField
            id="cancel-reason"
            fullWidth
            placeholder="Enter reason for cancelling this carrier booking..."
            value={cancelReason}
            onChange={e => {
              setCancelReason(e.target.value);
              if (submitError) setSubmitError('');
            }}
            onBlur={() => setCancelReasonTouched(true)}
            error={hasError}
            helperText={hasError ? 'Cancellation reason is required.' : ''}
            multiline
            minRows={4}
            maxRows={6}
            variant="outlined"
            disabled={isSubmitting}
            autoFocus
          />
        </Box>

        {submitError && (
          <Typography color="error" sx={{ fontSize: '13px', mb: 2, fontWeight: 500 }}>
            {submitError}
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <SbButton variant="outlined" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </SbButton>
          <SbButton
            variant="contained"
            color="error"
            onClick={handleConfirm}
            disabled={isSubmitting || !cancelReason.trim()}
          >
            {isSubmitting ? 'Cancelling...' : 'Confirm Cancellation'}
          </SbButton>
        </Box>
      </Box>
    </Modal>
  );
}
